import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAutoReplyMessageAsset,
  deleteAutoReplyMessageAsset,
  getAssetUpdateConfirmation,
  getAutoReplyMessageAssets,
  type TAssetUpdateImpact,
  updateAutoReplyMessageAsset,
} from 'api';
import { ConfirmModal } from 'components/ConfirmModal';
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import { Snackbar } from 'components/Snackbar';
import { pageCardClasses } from 'components/AppLayout/styles';
import { autoReplyMessageStrings } from 'constants/strings';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useRichMessageTemplates } from 'hooks/useRichMessageTemplates';
import {
  parseRichMessagePayload,
  readRichMessageRepairHints,
} from 'utils/richMessage';
import { issueMessage } from 'utils/scenarioIssues';
import { cn } from 'utils/cn';
import type { TRichMessageImagemapPayload } from 'types/richMessage';
import { RichMessageEditor } from './RichMessageEditor';

type SaveVariables = {
  targetId: number | null;
  payload: TRichMessageImagemapPayload;
  name: string;
  expectedImpact?: TAssetUpdateImpact;
};

export default function RichMessagePage() {
  const queryClient = useQueryClient();
  // 組織が変わると画面ごと作り直されるため、この画面の操作は常に表示中の組織へ送る
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [savedRevision, setSavedRevision] = useState(0);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    variables: SaveVariables;
    impact: TAssetUpdateImpact;
  }>();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    variant: 'success' | 'error';
  }>({
    open: false,
    message: '',
    variant: 'success',
  });

  const strings = lineStrings.management.richMessage;

  const { data: assets = [] } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS, 'RICH_MESSAGE'],
    queryFn: () => getAutoReplyMessageAssets('RICH_MESSAGE'),
  });
  const { data: templatesData } = useRichMessageTemplates();
  const richMessageTemplates = templatesData?.templates;

  const selectedAsset = assets.find((a) => a.id === editingId);
  const parsedInitial = selectedAsset
    ? parseRichMessagePayload(selectedAsset.payload, richMessageTemplates)
    : null;
  const repairHints =
    selectedAsset && !parsedInitial
      ? readRichMessageRepairHints(selectedAsset)
      : undefined;

  const saveMutation = useMutation({
    mutationFn: async ({
      targetId,
      payload,
      name,
      expectedImpact,
    }: SaveVariables) => {
      const body = {
        type: 'RICH_MESSAGE' as const,
        name,
        payload,
        previewImageUrl: payload.imageUrl,
        ...(expectedImpact ? { expectedImpact } : {}),
      };
      return targetId !== null
        ? updateAutoReplyMessageAsset(targetId, body, organizationId)
        : createAutoReplyMessageAsset(body, organizationId);
    },
    onSuccess: () => {
      setPendingUpdate(undefined);
      setSavedRevision((revision) => revision + 1);
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
      });
      setSnackbar({
        open: true,
        message: autoReplyMessageStrings.assets.saveSuccess,
        variant: 'success',
      });
    },
    onError: (error: Error, variables) => {
      const confirmation = getAssetUpdateConfirmation(error);
      if (confirmation) {
        setPendingUpdate({ variables, impact: confirmation.impact });
        return;
      }
      setSnackbar({
        open: true,
        message: issueMessage(error, strings.errors.saveFailed),
        variant: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (targetId: number) =>
      deleteAutoReplyMessageAsset(targetId, organizationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
      });
      resetEditor();
      setSnackbar({
        open: true,
        message: autoReplyMessageStrings.assets.deleteSuccess,
        variant: 'success',
      });
    },
    onError: (error: Error) =>
      setSnackbar({
        open: true,
        message: issueMessage(error, autoReplyMessageStrings.assets.deleteFailed),
        variant: 'error',
      }),
  });

  const resetEditor = () => {
    setEditingId(null);
    setEditorKey((k) => k + 1);
  };

  const isLocked =
    saveMutation.isPending || !!pendingUpdate || isUploadingImage;

  return (
    <>
      <section
        aria-busy={isLocked}
        className={cn(
          pageCardClasses.regular,
          'grid min-h-[calc(100vh-12rem)] gap-6 sm:p-6 lg:grid-cols-[300px_minmax(0,1fr)]',
          isLocked && 'pointer-events-none opacity-70',
        )}
      >
        <aside className="rounded-2xl border border-border/70 bg-white p-4">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-ink">
                {strings.createTitle}
              </h1>
              <p className="mt-1 text-xs text-muted">
                {strings.createDescription}
              </p>
            </div>
          </div>
          <div className="grid gap-2">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                disabled={isLocked}
                onClick={() => {
                  setEditingId(asset.id);
                  setEditorKey((k) => k + 1);
                }}
                className={`rounded-xl border p-3 text-left text-sm ${
                  editingId === asset.id
                    ? 'border-accent bg-accent-soft'
                    : 'border-border'
                }`}
              >
                <span className="block font-semibold">{asset.name}</span>
                <span className="text-xs text-muted">
                  {(asset.payload as TRichMessageImagemapPayload | null)?.altText ?? ''}
                </span>
              </button>
            ))}
          </div>
        </aside>

        <div className="rounded-2xl border border-border/70 bg-white p-5">
          {repairHints ? (
            <div
              role="alert"
              className="mb-5 grid gap-1 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            >
              <p className="font-semibold">{strings.repairWarning}</p>
              <p className="text-xs">{strings.repairHint}</p>
            </div>
          ) : null}
          <RichMessageEditor
            key={editorKey}
            initial={
              parsedInitial
                ? {
                    name: selectedAsset?.name,
                    altText: parsedInitial.altText,
                    templateId: parsedInitial.templateId,
                    imageUrl: parsedInitial.imageUrl,
                    imagemapBaseUrl: parsedInitial.imagemapBaseUrl,
                    areas: parsedInitial.areas,
                    customHeight: parsedInitial.customHeight,
                  }
                : repairHints
            }
            isSaving={saveMutation.isPending || !!pendingUpdate}
            canDelete={!!editingId}
            onDelete={() => editingId && deleteMutation.mutate(editingId)}
            onSave={(payload, name) =>
              saveMutation.mutate({ targetId: editingId, payload, name })
            }
            onUploadPendingChange={setIsUploadingImage}
            savedRevision={savedRevision}
          />
        </div>
      </section>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        variant={snackbar.variant}
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
      <ConfirmModal
        open={!!pendingUpdate}
        textTitle="この素材を使う配信内容が変わります"
        textMessage={
          pendingUpdate
            ? `シナリオ ${pendingUpdate.impact.scenarioCount}件、未完了Run ${pendingUpdate.impact.openRunCount}件に反映されます。保存しますか？`
            : undefined
        }
        textCancel="キャンセル"
        textConfirm="確認して保存"
        isLoading={saveMutation.isPending}
        onClose={() => setPendingUpdate(undefined)}
        onConfirm={() => {
          if (!pendingUpdate) return;
          saveMutation.mutate({
            ...pendingUpdate.variables,
            expectedImpact: pendingUpdate.impact,
          });
        }}
      />
    </>
  );
}
